import { useEffect, useMemo, type CSSProperties } from 'react';
import {
  getBender,
  getVerificationSummary,
  getSupportedSizes,
  getShoe,
} from '@509-bender/bender-library';
import type { BenderVerificationSummary, VerifiedShoeField } from '@509-bender/bender-library';
import {
  formatModelOptionLabel,
  getSupportedMaterials,
  listSelectableBenders,
  MATERIAL_LABELS,
  normalizeBenderSelection,
  type BenderSelection,
} from '../lib/bender-selection.js';
import { formatVerificationSummary, getVerificationWarning } from '../lib/bender-verification.js';

export type { BenderSelection } from '../lib/bender-selection.js';

type VerificationStatus = 'field-reference' | 'partial' | 'verified';
type ShoeWithVerification = NonNullable<ReturnType<typeof getShoe>> & {
  verifiedFields?: VerifiedShoeField[];
};

interface Props {
  value: BenderSelection;
  onChange: (next: BenderSelection) => void;
}

export function BenderPicker({ value, onChange }: Props) {
  const benders = listSelectableBenders();
  const selected = useMemo(() => normalizeBenderSelection(value), [value]);
  const model = getBender(selected.benderId);
  const materials = useMemo(() => getSupportedMaterials(selected.benderId), [selected.benderId]);
  const sizes = useMemo(
    () => getSupportedSizes(selected.benderId, selected.material),
    [selected.benderId, selected.material],
  );
  const shoe = getShoe(selected.benderId, selected.conduitSize, selected.material);

  useEffect(() => {
    if (!isSameSelection(value, selected)) {
      onChange(selected);
    }
  }, [onChange, selected, value]);

  return (
    <div
      style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-4)',
        marginBottom: 'var(--space-5)',
      }}
    >
      <div
        style={{
          fontSize: 'var(--text-xs)',
          fontWeight: 600,
          color: 'var(--color-text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          marginBottom: 'var(--space-3)',
        }}
      >
        Bender
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(82px, 0.5fr)',
          gap: 'var(--space-3)',
        }}
      >
        <SelectField
          label="Model"
          style={{ gridColumn: '1 / -1' }}
          value={selected.benderId}
          onChange={(v) => onChange(normalizeBenderSelection({ ...selected, benderId: v }))}
          options={benders.map((b) => ({ value: b.id, label: formatModelOptionLabel(b.id) }))}
        />
        <SelectField
          label="Material"
          value={selected.material}
          onChange={(v) =>
            onChange(
              normalizeBenderSelection({
                ...selected,
                material: v as BenderSelection['material'],
              }),
            )
          }
          options={materials.map((material) => ({
            value: material,
            label: MATERIAL_LABELS[material],
          }))}
        />
        <SelectField
          label="Size"
          value={selected.conduitSize}
          onChange={(v) => onChange(normalizeBenderSelection({ ...selected, conduitSize: v }))}
          options={
            sizes.length > 0
              ? sizes.map((s) => ({ value: s, label: `${s}"` }))
              : [{ value: '', label: 'no shoes' }]
          }
        />
      </div>
      {shoe ? (
        <ShoeConstantsRow shoe={shoe} />
      ) : (
        <div
          style={{
            marginTop: 'var(--space-3)',
            fontSize: 'var(--text-xs)',
            color: 'var(--color-error)',
          }}
        >
          This bender doesn't ship with the selected size + material.
        </div>
      )}
      {shoe && model && (
        <BenderDataNote
          notes={model.verificationNotes}
          source={model.source}
          sourceUrls={model.sourceUrls}
          status={model.verificationStatus ?? (model.verified ? 'verified' : 'field-reference')}
          summary={getVerificationSummary(model)}
        />
      )}
    </div>
  );
}

function ShoeConstantsRow({ shoe }: { shoe: ShoeWithVerification }) {
  const values = [
    {
      label: 'take-up',
      value: `${shoe.takeUp}"`,
      verified: shoe.verifiedFields?.includes('takeUp') ?? false,
    },
    {
      label: 'gain',
      value: `${shoe.gain}"`,
      verified: shoe.verifiedFields?.includes('gain') ?? false,
    },
    ...(typeof shoe.minRadius === 'number'
      ? [
          {
            label: 'radius',
            value: `${shoe.minRadius}"`,
            verified: shoe.verifiedFields?.includes('minRadius') ?? false,
          },
        ]
      : []),
  ];

  return (
    <div
      className="mono"
      style={{
        marginTop: 'var(--space-3)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'var(--space-2)',
        color: 'var(--color-text-muted)',
        fontSize: 'var(--text-xs)',
      }}
    >
      {values.map((item) => (
        <span key={item.label} style={constantPillStyle}>
          <span>
            {item.label} {item.value}
          </span>
          <span
            aria-label={
              item.verified
                ? `${item.label} manufacturer verified`
                : `${item.label} field reference`
            }
            title={item.verified ? 'Manufacturer source' : 'Field reference'}
            style={{
              color: item.verified ? 'var(--color-blue)' : 'var(--color-text-muted)',
              fontWeight: 900,
            }}
          >
            {item.verified ? 'source' : 'field'}
          </span>
        </span>
      ))}
    </div>
  );
}

function BenderDataNote({
  notes,
  source,
  sourceUrls,
  status,
  summary,
}: {
  notes?: string | undefined;
  source: string;
  sourceUrls?: string[] | undefined;
  status: VerificationStatus;
  summary: BenderVerificationSummary;
}) {
  const label =
    status === 'verified'
      ? 'Verified'
      : status === 'partial'
        ? 'Partially verified'
        : 'Field reference';
  const summaryText = formatVerificationSummary(summary);
  const warning = getVerificationWarning(summary);

  return (
    <div
      style={{
        marginTop: 'var(--space-3)',
        display: 'grid',
        gap: 'var(--space-1)',
        padding: 'var(--space-3)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--color-bg)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-3)',
        }}
      >
        <span
          style={{
            color: 'var(--color-text-muted)',
            fontSize: 'var(--text-xs)',
            fontWeight: 850,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          Constants
        </span>
        <span
          style={{
            color: verificationStatusColor(status),
            fontSize: 'var(--text-xs)',
            fontWeight: 900,
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
      </div>
      <div style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-xs)' }}>
        {notes ?? source}
      </div>
      <div className="mono" style={{ color: 'var(--color-text-muted)', fontSize: '0.68rem' }}>
        {summaryText}
      </div>
      {warning && <div style={verificationWarningStyle}>{warning}</div>}
      {notes && (
        <div style={{ color: 'var(--color-text-muted)', fontSize: '0.68rem' }}>
          Source: {source}
        </div>
      )}
      {sourceUrls && sourceUrls.length > 0 && (
        <div style={sourceLinkRowStyle}>
          {sourceUrls.slice(0, 3).map((url, index) => (
            <a key={url} href={url} target="_blank" rel="noreferrer" style={sourceLinkStyle}>
              Source {index + 1}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function verificationStatusColor(status: VerificationStatus): string {
  if (status === 'verified') return 'var(--color-blue)';
  if (status === 'partial') return 'var(--color-accent)';
  return 'var(--color-error)';
}

interface SelectFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  style?: CSSProperties;
}

function SelectField({ label, value, onChange, options, style }: SelectFieldProps) {
  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-1)',
        minWidth: 0,
        ...style,
      }}
    >
      <span
        style={{
          fontSize: 'var(--text-xs)',
          color: 'var(--color-text-muted)',
        }}
      >
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          minWidth: 0,
          background: 'var(--color-bg)',
          color: 'var(--color-text-primary)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-2) var(--space-3)',
          fontSize: 'var(--text-base)',
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function isSameSelection(a: BenderSelection, b: BenderSelection): boolean {
  return a.benderId === b.benderId && a.conduitSize === b.conduitSize && a.material === b.material;
}

const constantPillStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: 'var(--space-1) var(--space-2)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-pill)',
  background: 'var(--color-bg)',
};

const verificationWarningStyle: CSSProperties = {
  color: 'var(--color-error)',
  background: 'rgba(239, 68, 68, 0.1)',
  border: '1px solid rgba(239, 68, 68, 0.28)',
  borderRadius: 'var(--radius-sm)',
  padding: 'var(--space-2)',
  fontSize: 'var(--text-xs)',
  fontWeight: 750,
};

const sourceLinkRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--space-2)',
};

const sourceLinkStyle: CSSProperties = {
  color: 'var(--color-blue)',
  background: 'var(--color-blue-soft)',
  border: '1px solid rgba(0, 122, 255, 0.28)',
  borderRadius: 'var(--radius-pill)',
  padding: 'var(--space-1) var(--space-2)',
  fontSize: '0.68rem',
  fontWeight: 850,
};
